import { useState } from "react";
import { CircleCheck, SquarePen } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useElevateToDecision } from "./chat-api";

/**
 * Turning an answer into a decision.
 *
 * Two acts, and the second is **not an edit of the first**. *Accept* files the
 * decision the Brain drafts from the answer. *Modify & accept* has the Brain
 * draft it from the answer and the person's change, and keeps the change
 * **beside** it, in their words and attributed to them. The title and the
 * statement state the decision as changed. The answer it came from is stored
 * with the decision, and the decision dialog shows it under the sources, as
 * "The exchange it was taken from" (`data-model/0014` §7).
 *
 * That asymmetry is the design and it is worth defending: a decision a human
 * edited in place looks exactly like one the system wrote, so the record loses
 * the two things it is for — what was proposed, and where somebody disagreed.
 * See `data-model/0009`.
 *
 * Nothing here composes the decision text. It is written server-side from the
 * question, the answer, its sources and the person's note; a client that could
 * supply it could file a "decision by GroundBrain" the Brain never wrote into
 * the corpus it later cites.
 *
 * **Side by side, in the row under the answer** (2026-09-27, `product/0013`),
 * with "Mark as wrong". They were stacked full width at the top of the source
 * rail, which is gone.
 */
export function AcceptDecision({
  projectId,
  questionId,
  decided,
}: {
  projectId: string;
  questionId: string;
  /** Already elevated. The pair is replaced by a statement of fact. */
  decided: boolean;
}) {
  const [modifying, setModifying] = useState(false);
  const elevate = useElevateToDecision(projectId);

  if (decided) {
    // The *same control*, settled — same shape, same green, spent. A different
    // element here (a pill, a line of text) reads as the button having vanished
    // and something else appearing in its place; this reads as the thing you
    // pressed having taken effect, which is what happened.
    //
    // `disabled` rather than merely styled-down: it is genuinely not pressable
    // again, the server refuses a second elevation with a 409, and a control
    // that looks spent but still takes a click earns that error for no reason.
    return (
      <Button
        type="button"
        disabled
        aria-disabled
        className="justify-center bg-ok text-white"
      >
        <CircleCheck size={15} aria-hidden />
        Accepted
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          className="justify-center bg-ok hover:bg-ok-strong"
          disabled={elevate.isPending}
          onClick={() => elevate.mutate({ questionId })}
        >
          <CircleCheck size={15} aria-hidden />
          {elevate.isPending && !modifying ? "Writing the decision…" : "Accept decision"}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="justify-center"
          disabled={elevate.isPending}
          onClick={() => setModifying(true)}
        >
          <SquarePen size={15} aria-hidden />
          Modify &amp; accept
        </Button>
      </div>
      {elevate.isPending && !modifying && (
        // **Measured at ~15s.** Accepting is not a save — a model reads the
        // answer and its sources and writes the decision statement. A button
        // that says "Filing…" for fifteen seconds reads as hung, and the reader
        // starts clicking it again.
        <p className="text-[0.8rem] text-muted" role="status" aria-live="polite">
          Reading the answer and its sources — this takes a few seconds.
        </p>
      )}

      {elevate.isError && (
        <p role="alert" className="text-[0.82rem] font-semibold text-danger">
          {/* Named rather than generic, because the two failures need different
              responses from the reader: nothing was filed, or something already
              was. */}
          {elevate.error instanceof Error
            ? elevate.error.message
            : "That could not be filed as a decision."}
        </p>
      )}

      {modifying && (
        <ModifyDialog
          pending={elevate.isPending}
          onCancel={() => setModifying(false)}
          onAccept={(note) =>
            elevate.mutate(
              { questionId, modificationNote: note },
              { onSuccess: () => setModifying(false) },
            )
          }
        />
      )}
    </div>
  );
}

function ModifyDialog({
  pending,
  onCancel,
  onAccept,
}: {
  pending: boolean;
  onCancel: () => void;
  onAccept: (note: string) => void;
}) {
  const [note, setNote] = useState("");
  const ready = note.trim().length > 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modify &amp; accept decision</DialogTitle>
          <DialogDescription>
            The Brain drafts the decision from the answer and your change. Your note is
            kept with it, as you wrote it, and attributed to you.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <label className="block">
            <span className="mb-1.5 block font-display text-[0.66rem] font-semibold tracking-[0.1em] text-muted uppercase">
              Describe your modification
            </span>
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={4}
              autoFocus
              placeholder="e.g. Capped at 17 Nm for the perimeter rows after the wind-load review — see RAMS rev. C."
            />
          </label>
          <p className="mt-2 text-[0.82rem] text-muted">
            {/* Says what happens to the note: the decision is drafted from it,
                and the note itself is kept word for word, beside the decision.
                The answer stays readable in the decision dialog, as the
                exchange it was taken from (`data-model/0014` §7). */}
            Kept word for word beside the decision. The answer it changes stays readable too.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            className={cn("bg-ok hover:bg-ok-strong")}
            // Disabled until something is written: an empty note would file
            // "accepted with changes" with no change named, and the state is
            // derived from the note existing at all.
            disabled={!ready || pending}
            onClick={() => onAccept(note.trim())}
          >
            <CircleCheck size={15} aria-hidden />
            {pending ? "Writing the decision…" : "Accept decision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
